import { useMemo, useState } from 'react';
import { INK, MUTED, SUBTLE, INSET, CHIP, PillButton } from './club/clubUi';
import { Card, CardHead, RoundX, FLASH } from './pro/setup/setupUi';

/**
 * SPECIFIC DATES — the month view of a Pro's availability (design_handoff_pro_setup
 * "AVAILABILITY"). The weekly hours fill the calendar, the grid SHOWS the resulting shape, and
 * tapping a day opens that one date: WEEKLY HOURS / CLOSED / CUSTOM HOURS. The grid is a
 * picture of the answer, not a second place to type it.
 *
 * Each cell reads: the hours it's open (black = the week, #FF00AA = a date you set) and a grey
 * 3px bar where a connected calendar says the time is already gone. The dates that break the
 * pattern list below as EXCEPT ON THESE DAYS.
 */
const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAY_FULL = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const DAY_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTHS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
const DEFAULT_RANGE = { startMinute: 540, endMinute: 1020 };

const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

export function todayIso(now = new Date()) {
  return iso(now.getFullYear(), now.getMonth(), now.getDate());
}

/** 540 → "9a". Minutes are the storage unit; this is only for reading. */
export function clock(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const suffix = h < 12 ? 'a' : 'p';
  const hour = h % 12 || 12;
  return m ? `${hour}:${String(m).padStart(2, '0')}${suffix}` : `${hour}${suffix}`;
}

const toTime = (minutes) => `${String(Math.floor((minutes || 0) / 60)).padStart(2, '0')}:${String((minutes || 0) % 60).padStart(2, '0')}`;
const toMinutes = (value) => { const [h, m] = String(value || '0:0').split(':').map(Number); return (h || 0) * 60 + (m || 0); };

/**
 * What a date says: its own rows if it has any, otherwise the weekly pattern. Mirrors
 * ProAvailabilityRules.WindowsOn on the server — the two must agree.
 */
export function windowsFor(dateIso, windows) {
  const all = windows || [];
  const dated = all.filter((w) => w.date === dateIso);
  if (dated.some((w) => w.isClosed)) return { ranges: [], override: true, closed: true };
  if (dated.length > 0) return { ranges: sortRanges(dated), override: true, closed: false };
  const [y, m, d] = dateIso.split('-').map(Number);
  const dow = new Date(y, m - 1, d).getDay();
  const weekly = all.filter((w) => !w.date && w.dayOfWeek === dow);
  return { ranges: sortRanges(weekly), override: false, closed: weekly.length === 0 };
}

const sortRanges = (rows) => rows
  .filter((r) => !r.isClosed && r.endMinute > r.startMinute)
  .map((r) => ({ startMinute: r.startMinute, endMinute: r.endMinute }))
  .sort((a, b) => a.startMinute - b.startMinute);

const parseIso = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const timePill = { width: 130, height: 40, padding: '0 12px', border: 'none', borderRadius: 999, background: '#fff', fontFamily: 'inherit', fontSize: 12, color: INK, boxSizing: 'border-box', outline: 'none' };
const optPill = (on) => ({ height: 38, padding: '0 16px', border: 'none', borderRadius: 999, background: on ? INK : CHIP, color: on ? '#fff' : INK, fontFamily: 'inherit', fontSize: 9, fontWeight: 800, letterSpacing: '.14em', cursor: 'pointer', textTransform: 'uppercase' });

export default function AvailabilityCalendar({ windows = [], busy = [], onSetDate, onClearDate, monthsAhead = 12, phone = false }) {
  const today = todayIso();
  const now = new Date();
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState(null); // { date, closed, ranges, hadOverride }

  const cursor = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const busyByDate = useMemo(() => {
    const map = new Map();
    (busy || []).forEach((b) => { const list = map.get(b.date) || []; list.push(b); map.set(b.date, list); });
    return map;
  }, [busy]);

  const cells = useMemo(() => {
    const first = new Date(year, month, 1).getDay();
    const days = new Date(year, month + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < first; i += 1) out.push(null);
    for (let d = 1; d <= days; d += 1) out.push(d);
    return out;
  }, [year, month]);

  const overrides = useMemo(() => {
    const byDate = new Map();
    (windows || []).filter((w) => w.date).forEach((w) => { const e = byDate.get(w.date) || { date: w.date, closed: false, ranges: [] }; if (w.isClosed) e.closed = true; else e.ranges.push(w); byDate.set(w.date, e); });
    return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date)).map((e) => ({ ...e, ranges: sortRanges(e.ranges) }));
  }, [windows]);

  const openEditor = (dateIso) => {
    if (editing?.date === dateIso) { setEditing(null); return; }
    const state = windowsFor(dateIso, windows);
    setEditing({ date: dateIso, closed: state.closed && state.override, ranges: state.ranges.length ? state.ranges.map((r) => ({ ...r })) : [{ ...DEFAULT_RANGE }], hadOverride: state.override });
  };
  const save = async () => { if (!editing) return; await onSetDate?.(editing.date, editing.closed, editing.closed ? [] : editing.ranges); setEditing(null); };
  const reset = async () => { if (!editing) return; await onClearDate?.(editing.date); setEditing(null); };
  const jumpTo = (dateIso) => {
    const d = parseIso(dateIso);
    setOffset(Math.max(0, Math.min(monthsAhead, (d.getFullYear() - now.getFullYear()) * 12 + d.getMonth() - now.getMonth())));
    const state = windowsFor(dateIso, windows);
    setEditing({ date: dateIso, closed: state.closed && state.override, ranges: state.ranges.length ? state.ranges.map((r) => ({ ...r })) : [{ ...DEFAULT_RANGE }], hadOverride: state.override });
  };

  const sel = editing ? parseIso(editing.date) : null;
  const selState = editing ? (editing.closed ? 'closed' : editing.hadOverride ? 'custom' : 'week') : null;
  const weekLabel = sel ? (() => { const dow = sel.getDay(); const w = sortRanges((windows || []).filter((x) => !x.date && x.dayOfWeek === dow)); return w.length ? w.map((r) => `${clock(r.startMinute)}–${clock(r.endMinute)}`).join(', ').toUpperCase() : 'CLOSED'; })() : '';
  const round = (dis) => ({ width: 36, height: 36, border: 'none', borderRadius: '50%', background: CHIP, opacity: dis ? 0.3 : 1, fontFamily: 'inherit', fontSize: 14, cursor: dis ? 'default' : 'pointer' });

  return (
    <Card phone={phone} style={{ gap: 12 }}>
      <CardHead title="SPECIFIC DATES" right={overrides.length ? <span style={{ color: FLASH }}>{overrides.length} {overrides.length === 1 ? 'DATE' : 'DATES'} SET</span> : null} />
      <span style={{ fontSize: 12, lineHeight: 1.6, color: MUTED }}>Every day follows your weekly hours until you tap it. Tap a date to close it or give it its own hours — a holiday, or a day you only work the morning.</span>
      <div className="flex items-center justify-between" style={{ gap: 10 }}>
        <button type="button" onClick={() => setOffset((o) => Math.max(0, o - 1))} disabled={offset <= 0} aria-label="Previous month" style={round(offset <= 0)}>‹</button>
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.16em' }}>{MONTHS[month]} {year}</span>
        <button type="button" onClick={() => setOffset((o) => Math.min(monthsAhead, o + 1))} disabled={offset >= monthsAhead} aria-label="Next month" style={round(offset >= monthsAhead)}>›</button>
      </div>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 4 }}>
        {DAY_LABELS.map((d, i) => <span key={i} className="text-center" style={{ padding: '4px 0', fontSize: 9, fontWeight: 700, letterSpacing: '.12em', color: SUBTLE }}>{d}</span>)}
        {cells.map((dayNum, i) => {
          if (dayNum == null) return <span key={`pad-${i}`} />;
          const key = iso(year, month, dayNum);
          const state = windowsFor(key, windows);
          const blocks = busyByDate.get(key) || [];
          const past = key < today;
          const isToday = key === today;
          const selected = editing?.date === key;
          const label = state.ranges.length ? state.ranges.map((r) => `${clock(r.startMinute)}–${clock(r.endMinute)}`).join(' ') : '';
          return (
            <button key={key} type="button" disabled={past} onClick={() => openEditor(key)} aria-label={`Set hours for ${key}`} className="relative flex flex-col items-start overflow-hidden text-left font-mono"
              style={{ gap: 5, minWidth: 0, height: phone ? 48 : 62, padding: 6, border: 'none', borderRadius: 12, background: INSET, boxShadow: selected ? 'inset 0 0 0 2px #000' : 'none', opacity: past ? 0.35 : 1, cursor: past ? 'default' : 'pointer' }}>
              <span style={{ padding: '1px 5px', borderRadius: 6, background: isToday ? INK : 'transparent', color: isToday ? '#fff' : INK, fontSize: 10, fontWeight: 700 }}>{dayNum}</span>
              <span className="block w-full truncate" style={{ minHeight: phone ? 6 : 'auto', padding: phone ? 0 : '2px 4px', borderRadius: 6, background: state.ranges.length ? (state.override ? FLASH : INK) : 'transparent', color: state.ranges.length ? '#fff' : SUBTLE, fontSize: 8, fontWeight: 700, letterSpacing: '.04em', boxSizing: 'border-box' }}>{phone ? '' : (label || (state.closed ? 'Closed' : ''))}</span>
              {blocks.length > 0 && <span title={blocks.map((b) => (b.isAllDay ? 'Busy all day' : `Busy ${clock(b.startMinute)}–${clock(b.endMinute)}`)).join(', ')} className="absolute" style={{ left: 6, right: 6, bottom: 5, height: 3, borderRadius: 2, background: SUBTLE }}>
                <span className="sr-only">{blocks.map((b) => (b.isAllDay ? 'busy' : `${clock(b.startMinute)}–${clock(b.endMinute)}`)).join(' ')}</span>
              </span>}
            </button>
          );
        })}
      </div>

      {editing && sel && (
        <div className="flex flex-col" style={{ gap: 12, padding: '16px 18px', borderRadius: 20, background: INSET, boxShadow: 'inset 0 0 0 2px #000' }} data-testid="date-editor">
          <span className="flex items-start justify-between" style={{ gap: 10 }}>
            <span className="flex flex-col" style={{ gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.12em' }}>{DAY_FULL[sel.getDay()]} {MONTHS[sel.getMonth()]} {sel.getDate()}</span>
              <span style={{ fontSize: 10, letterSpacing: '.1em', color: MUTED }}>{selState === 'week' ? `FOLLOWS YOUR WEEK · ${weekLabel}` : selState === 'closed' ? 'CLOSED THIS DAY' : 'CUSTOM HOURS THIS DAY'} · <span>{editing.date}</span></span>
            </span>
            <RoundX size={30} bg="#fff" aria-label="Close" onClick={() => setEditing(null)} />
          </span>
          <span className="flex flex-wrap" style={{ gap: 6 }}>
            {editing.hadOverride && <button type="button" onClick={reset} aria-label="Follow the weekly hours" style={optPill(false)}>WEEKLY HOURS</button>}
            <button type="button" role="checkbox" aria-checked={editing.closed} aria-label="Closed all day" onClick={() => setEditing((s) => ({ ...s, closed: !s.closed }))} style={optPill(editing.closed)}>CLOSED</button>
            <button type="button" onClick={() => setEditing((s) => ({ ...s, closed: false }))} style={optPill(!editing.closed)}>CUSTOM HOURS</button>
          </span>
          {!editing.closed && (
            <div className="flex flex-col" style={{ gap: 6 }}>
              {editing.ranges.map((r, ri) => (
                <span key={ri} className="flex flex-wrap items-center" style={{ gap: 6 }}>
                  <input type="time" value={toTime(r.startMinute)} aria-label="From" onChange={(e) => setEditing((s) => ({ ...s, ranges: s.ranges.map((x, xi) => (xi === ri ? { ...x, startMinute: toMinutes(e.target.value) } : x)) }))} className="pr-pill-input font-mono" style={timePill} />
                  <span style={{ fontSize: 10, color: SUBTLE }}>to</span>
                  <input type="time" value={toTime(r.endMinute)} aria-label="To" onChange={(e) => setEditing((s) => ({ ...s, ranges: s.ranges.map((x, xi) => (xi === ri ? { ...x, endMinute: toMinutes(e.target.value) } : x)) }))} className="pr-pill-input font-mono" style={timePill} />
                  {editing.ranges.length > 1 && <RoundX size={30} bg="#fff" aria-label="Remove hours" onClick={() => setEditing((s) => ({ ...s, ranges: s.ranges.filter((_, xi) => xi !== ri) }))} />}
                </span>
              ))}
              <button type="button" onClick={() => setEditing((s) => ({ ...s, ranges: [...s.ranges, { ...DEFAULT_RANGE }] }))} className="self-start font-mono" style={{ height: 28, padding: '0 12px', border: 'none', borderRadius: 999, background: INK, color: '#fff', fontSize: 9, fontWeight: 800, letterSpacing: '.12em', cursor: 'pointer' }}>+ HOURS</button>
            </div>
          )}
          <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
            <PillButton size={40} onClick={save} style={{ textTransform: 'uppercase' }}>Save this date</PillButton>
          </span>
        </div>
      )}

      {overrides.length > 0 && (
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span style={{ paddingBottom: 6, fontSize: 10, fontWeight: 700, letterSpacing: '.16em' }}>EXCEPT ON THESE DAYS</span>
          {overrides.map((o) => {
            const d = parseIso(o.date);
            return (
              <span key={o.date} className="flex items-center" style={{ gap: 10, padding: '8px 8px 8px 16px', borderRadius: 999, background: INSET }}>
                <button type="button" onClick={() => jumpTo(o.date)} className="flex flex-1 text-left font-mono" style={{ gap: 12, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}>
                  <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em' }}>{DAY_SHORT[d.getDay()]} {MONTHS[d.getMonth()].slice(0, 3)} {d.getDate()}</span>
                  <span style={{ fontSize: 11, color: MUTED }}>{o.closed ? 'CLOSED' : o.ranges.map((r) => `${clock(r.startMinute)}–${clock(r.endMinute)}`).join(', ').toUpperCase()}</span>
                </button>
                <RoundX size={28} bg="#fff" aria-label={`Back to weekly hours on ${o.date}`} onClick={() => onClearDate?.(o.date)} />
              </span>
            );
          })}
        </div>
      )}

      <span className="flex flex-wrap" style={{ gap: '6px 16px', fontSize: 10, letterSpacing: '.08em', color: MUTED }}>
        <span className="flex items-center" style={{ gap: 6 }}><span style={{ width: 14, height: 8, borderRadius: 3, background: INK }} />WEEKLY HOURS</span>
        <span className="flex items-center" style={{ gap: 6 }}><span style={{ width: 14, height: 8, borderRadius: 3, background: FLASH }} />A DATE YOU SET</span>
        <span className="flex items-center" style={{ gap: 6 }}><span style={{ width: 14, height: 3, borderRadius: 2, background: SUBTLE }} />BUSY ON A CONNECTED CALENDAR</span>
      </span>
    </Card>
  );
}
